import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

/**
 * Handles redirect from backend after Microsoft SSO.
 * Reads access_token and refresh_token from window.location.hash,
 * stores in localStorage, clears hash, calls getCurrentUser to load user into AuthContext, redirects to /.
 */
function parseHashParams() {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return {};
  return Object.fromEntries(new URLSearchParams(hash));
}

const MicrosoftCallback = () => {
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = parseHashParams();
    const access = params.access_token;
    const refresh = params.refresh_token;
    const err = params.error;

    if (err === 'no_group' || err === 'access_denied') {
      navigate('/access-denied', { replace: true });
      return;
    }
    if (err) {
      setError(err);
      return;
    }
    if (!access || !refresh) {
      setError('Missing tokens');
      return;
    }

    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.removeItem('mfa_verified');
    window.history.replaceState({}, document.title, window.location.pathname);

    loadUser().then((user) => {
      if (user?.role === 'transportation') {
        navigate('/transportation', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }).catch((e) => {
      const code = e.response?.data?.code;
      if (code === 'no_group' || code === 'access_denied') {
        navigate('/access-denied', { replace: true });
      } else {
        setError('Failed to load user');
      }
    });
  }, [loadUser, navigate]);

  const errorMessages = {
    wrong_tenant: 'This account is not from the allowed organization.',
    invalid_state: 'Invalid state. Please try signing in again.',
    token_exchange_failed: 'Token exchange failed. Please try again.',
    no_id_token: 'No identity token received.',
    invalid_id_token: 'Invalid identity token.',
    no_email: 'No email in account.',
    no_group: 'Your account has not been assigned access to this system. Please contact your administrator.',
  };

  if (error) {
    const message = errorMessages[error] || error;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          Sign-in failed: {message}
        </Typography>
        <Button variant="contained" href="/login" sx={{ mt: 1 }}>
          Try again
        </Button>
        <Typography component="a" href="/login" sx={{ color: 'primary.main', mt: 2, display: 'block' }}>
          Return to login
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Signing you in...</Typography>
    </Box>
  );
};

export default MicrosoftCallback;
